import React, { useState } from 'react';
import { firestore } from '../../firebase/config';
import emailService from '../../services/emailService';
import AppointmentEditModal from './AppointmentEditModal';
import './AppointmentDetails.css';

const AppointmentDetails = ({ appointment, onClose, onUpdate }) => {
  const [status, setStatus] = useState(appointment.status);
  const [notes, setNotes] = useState(appointment.notes || '');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Formater la durée
  const formatDuration = (duration) => {
    if (!duration) return '';
    if (duration === 90) return '1h30';
    if (duration === 60) return '1h';
    if (duration === 45) return '45min';
    return `${duration} min`;
  };

  // Vérifier si un rendez-vous peut être modifié (24h avant)
  const canModifyAppointment = () => {
    const appointmentDate = new Date(`${appointment.date}T${appointment.time}`);
    const now = new Date();
    const diffTime = appointmentDate - now;
    const diffHours = diffTime / (1000 * 60 * 60);
    return diffHours > 24;
  };

  // Vérifier si c'est un rendez-vous à venir
  const isUpcomingAppointment = () => {
    const appointmentDate = new Date(`${appointment.date}T${appointment.time}`);
    const now = new Date();
    return appointmentDate > now && appointment.status === 'confirmed';
  };

  // Gérer la mise à jour après modification
  const handleAppointmentUpdate = (updatedAppointment) => {
    if (onUpdate) {
      onUpdate(updatedAppointment);
    }
    setShowEditModal(false);
  };

  // Mettre à jour le rendez-vous
  const handleUpdate = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Déterminer qui a annulé en fonction du statut sélectionné
      const updateData = {
        status: status === 'canceled_client' || status === 'canceled_practitioner' || status === 'client_no_show' ? 'canceled' : status,
        notes,
        updatedAt: new Date()
      };

      // Ajouter les informations sur qui a annulé
      if (status === 'canceled_client') {
        updateData.canceledBy = 'client';
      } else if (status === 'canceled_practitioner') {
        updateData.canceledBy = 'practitioner';
      } else if (status === 'client_no_show') {
        updateData.canceledBy = 'no_show';
      }

      await firestore
        .collection('appointments')
        .doc(appointment.id)
        .update(updateData);

      // Si le statut a changé pour "annulé", envoyer des emails
      if (updateData.status === 'canceled' && appointment.status !== 'canceled') {
        try {
          // Préparer les données du rendez-vous avec son ID
          const updatedAppointment = {
            ...appointment,
            ...updateData,
            id: appointment.id
          };

          // Envoyer un email au client
          emailService.sendCancellationEmail(
            updatedAppointment,
            'client',
            updateData.canceledBy,
            notes // Utiliser les notes comme raison d'annulation
          );

          console.log('Email d\'annulation envoyé au client');

          // Envoyer un email au praticien si l'annulation vient du client
          if (updateData.canceledBy === 'client') {
            emailService.sendCancellationEmail(
              updatedAppointment,
              'practitioner',
              updateData.canceledBy,
              notes
            );
            console.log('Email d\'annulation envoyé au praticien');
          }
        } catch (emailError) {
          console.error('Erreur lors de l\'envoi des emails d\'annulation:', emailError);
          // Ne pas bloquer le processus si l'envoi d'email échoue
        }
      }

      setSuccess(true);

      if (onUpdate) {
        onUpdate({
          ...appointment,
          ...updateData
        });
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour du rendez-vous:', err);
      setError('Une erreur est survenue lors de la mise à jour du rendez-vous.');
    } finally {
      setLoading(false);
    }
  };

  // Annuler le rendez-vous
  const handleCancel = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler ce rendez-vous ?')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const updateData = {
        status: 'canceled',
        canceledBy: 'practitioner',
        updatedAt: new Date()
      };

      await firestore
        .collection('appointments')
        .doc(appointment.id)
        .update(updateData);

      // Envoyer un email d'annulation au client
      try {
        // Préparer les données du rendez-vous avec son ID
        const updatedAppointment = {
          ...appointment,
          ...updateData,
          id: appointment.id
        };

        emailService.sendCancellationEmail(
          updatedAppointment,
          'client',
          'practitioner',
          notes // Utiliser les notes comme raison d'annulation
        );

        console.log('Email d\'annulation envoyé au client');
      } catch (emailError) {
        console.error('Erreur lors de l\'envoi de l\'email d\'annulation:', emailError);
        // Ne pas bloquer le processus si l'envoi d'email échoue
      }

      setStatus('canceled_practitioner');
      setSuccess(true);

      if (onUpdate) {
        onUpdate({
          ...appointment,
          status: 'canceled',
          canceledBy: 'practitioner'
        });
      }
    } catch (err) {
      console.error('Erreur lors de l\'annulation du rendez-vous:', err);
      setError('Une erreur est survenue lors de l\'annulation du rendez-vous.');
    } finally {
      setLoading(false);
    }
  };

  // Fonction d'affichage du statut détaillé
  const getDetailedStatus = () => {
    if (appointment.status === 'canceled') {
      if (appointment.canceledBy === 'client') {
        return 'canceled_client';
      } else if (appointment.canceledBy === 'practitioner') {
        return 'canceled_practitioner';
      } else if (appointment.canceledBy === 'no_show') {
        return 'client_no_show';
      }
      return 'canceled_practitioner';
    }
    return appointment.status;
  };

  // Initialisation du statut détaillé au chargement
  useState(() => {
    setStatus(getDetailedStatus());
  }, [appointment]);

  return (
    <>
      <div className="appointment-details-modal">
        <div className="appointment-details-content">
          <div className="appointment-details-header">
            <h3>Détails du rendez-vous</h3>
            <button
              className="close-modal"
              onClick={onClose}
              aria-label="Fermer"
            >
              &times;
            </button>
          </div>

          {success && (
            <div className="success-message">
              Le rendez-vous a été mis à jour avec succès.
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="appointment-info">
            <div className="info-row">
              <div className="info-label">Date :</div>
              <div className="info-value">{appointment.formattedDate}</div>
            </div>

            <div className="info-row">
              <div className="info-label">Heure :</div>
              <div className="info-value">{appointment.time}</div>
            </div>

            <div className="info-row">
              <div className="info-label">Service :</div>
              <div className="info-value">{appointment.serviceTitle}</div>
            </div>

            <div className="info-row">
              <div className="info-label">Durée :</div>
              <div className="info-value">{formatDuration(appointment.duration)}</div>
            </div>

            <div className="info-row">
              <div className="info-label">Prix :</div>
              <div className="info-value">{appointment.price}€</div>
            </div>

            <div className="info-row">
              <div className="info-label">Type :</div>
              <div className="info-value">
                {appointment.consultationMode === 'remote' ? (
                  <span className="detail-remote-badge">À distance (téléconsultation)</span>
                ) : (
                  <span className="detail-in-person-badge">En présentiel</span>
                )}
              </div>
            </div>

            {/* Section pour le lien de visioconférence (si rendez-vous à distance) */}
            {appointment.consultationMode === 'remote' && (
              <div className="remote-info-panel">
                <div className="remote-info-panel-header">
                  <h4>Consultation à distance</h4>
                </div>

                <div className="remote-info-content">
                  {appointment.meetingLink ? (
                    <>
                      <p>Ce rendez-vous se déroule en visioconférence. Utilisez le lien ci-dessous pour démarrer la consultation :</p>
                      <div className="remote-actions">
                        <a
                          href={appointment.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                        >
                          Démarrer la visioconférence
                        </a>
                        {/* Option pour copier le lien */}
                        <button
                          className="btn btn-outline btn-copy-link"
                          onClick={() => {
                            navigator.clipboard.writeText(appointment.meetingLink)
                              .then(() => alert('Lien copié dans le presse-papiers !'))
                              .catch(err => console.error('Erreur lors de la copie :', err));
                          }}
                          title="Copier le lien dans le presse-papiers"
                        >
                          Copier le lien
                        </button>
                      </div>
                      <div className="meeting-link-display">
                        <input
                          type="text"
                          value={appointment.meetingLink}
                          readOnly
                          className="form-control meeting-link-input"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <p>Ce rendez-vous est prévu en visioconférence, mais aucun lien n'a encore été généré.</p>
                      <button
                        className="btn btn-primary"
                        onClick={async () => {
                          try {
                            // Générer un lien Google Meet basé sur l'ID du rendez-vous
                            const meetLink = `https://meet.google.com/naturobook-${appointment.id.replace(/[^a-zA-Z0-9]/g, '').substring(0, 10).toLowerCase()}`;

                            // Mettre à jour le document du rendez-vous
                            await firestore
                              .collection('appointments')
                              .doc(appointment.id)
                              .update({
                                meetingLink: meetLink,
                                updatedAt: new Date()
                              });

                            // Mettre à jour l'état local avec le nouveau lien
                            if (onUpdate) {
                              onUpdate({
                                ...appointment,
                                meetingLink: meetLink
                              });
                            }

                            alert('Lien de visioconférence généré avec succès !');
                          } catch (error) {
                            console.error('Erreur lors de la génération du lien :', error);
                            alert('Une erreur est survenue lors de la génération du lien.');
                          }
                        }}
                      >
                        Générer un lien de visioconférence
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Afficher les notes de service si elles existent */}
            {appointment.serviceNotes && (
              <div className="service-notes-container" style={{ marginTop: '15px' }}>
                <div className="info-label">Informations complémentaires :</div>
                <div className="service-notes" style={{
                  whiteSpace: 'pre-line',
                  backgroundColor: '#f0f7f3',
                  padding: '10px',
                  borderRadius: '5px',
                  marginTop: '5px',
                  borderLeft: '3px solid #3a865f'
                }}>
                  {appointment.serviceNotes}
                </div>
              </div>
            )}
          </div>

          <div className="client-info">
            <h4>Informations client</h4>

            <div className="info-row">
              <div className="info-label">Nom :</div>
              <div className="info-value">{appointment.name}</div>
            </div>

            <div className="info-row">
              <div className="info-label">Email :</div>
              <div className="info-value">{appointment.email}</div>
            </div>

            <div className="info-row">
              <div className="info-label">Téléphone :</div>
              <div className="info-value">{appointment.phone}</div>
            </div>
          </div>

          <div className="appointment-form">
            <div className="form-group">
              <label htmlFor="status">Statut</label>
              <select
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="form-control"
                disabled={loading}
              >
                <option value="confirmed">Confirmé</option>
                <option value="canceled_client">Annulé par le client</option>
                <option value="canceled_practitioner">Annulé par le praticien</option>
                <option value="client_no_show">Client absent</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="form-control"
                rows="3"
                placeholder="Notes confidentielles sur ce rendez-vous"
                disabled={loading}
              ></textarea>
            </div>
          </div>

          <div className="appointment-actions">
            {isUpcomingAppointment() && canModifyAppointment() && (
              <button
                className="btn btn-outline"
                onClick={() => setShowEditModal(true)}
                disabled={loading}
              >
                Modifier le rendez-vous
              </button>
            )}

            {status === 'confirmed' && (
              <button
                className="btn btn-danger"
                onClick={handleCancel}
                disabled={loading}
              >
                Annuler le rendez-vous
              </button>
            )}

            <button
              className="btn btn-primary"
              onClick={handleUpdate}
              disabled={loading}
            >
              {loading ? 'Mise à jour...' : 'Enregistrer les modifications'}
            </button>
          </div>
        </div>
      </div>

      {/* Modal de modification */}
      {showEditModal && (
        <AppointmentEditModal
          appointment={appointment}
          onClose={() => setShowEditModal(false)}
          onUpdate={handleAppointmentUpdate}
          userRole="practitioner"
        />
      )}
    </>
  );
};

export default AppointmentDetails;